import React from 'react'
import { createRoot } from 'react-dom/client'
import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import { ConfigProvider } from 'antd'
import App from './components/App'
import reportWebVitals from './reportWebVitals'

// The CSP nonce is stamped into this meta tag per request by nginx
// (see nginx/nginx.conf). Both Emotion and Ant Design need it up front, so
// every <style> tag either of them injects carries a nonce the CSP will
// accept. Ant Design v5 uses its own cssinjs engine internally, not
// Emotion, and exposes its nonce config via ConfigProvider's csp prop
// (https://ant.design/components/config-provider#csp), not via cssinjs
// directly. Nested ConfigProviders elsewhere in this app (none of which set
// their own csp prop) inherit this root one.
// Outside nginx (local dev, tests) the placeholder is never substituted,
// so treat the literal placeholder as "no nonce" rather than pass it through.
const cspNonceRaw = document
  .querySelector('meta[name="csp-nonce"]')
  ?.getAttribute('content')
const cspNonce =
  cspNonceRaw && cspNonceRaw !== '__CSP_NONCE__' ? cspNonceRaw : undefined

const emotionCache = createCache({ key: 'orgadmin', nonce: cspNonce })

// webpack adds a link tag for each lazy route's own CSS file when that
// route loads (Organization, OrganizationConnections, LicenseDetails, and
// others use React.lazy). webpack puts the nonce on that tag by rewriting
// this exact bare identifier at build time. It is not a real global, so it
// cannot be set as a property on window or globalThis. eslint cannot see
// that webpack itself reads it, hence the disable below.
/* eslint-disable @typescript-eslint/no-unused-vars, prefer-const */
declare let __webpack_nonce__: string | undefined
__webpack_nonce__ = cspNonce
/* eslint-enable @typescript-eslint/no-unused-vars, prefer-const */

const root = createRoot(document.getElementById('root') as HTMLElement)
root.render(
  <React.StrictMode>
    <CacheProvider value={emotionCache}>
      <ConfigProvider csp={{ nonce: cspNonce }}>
        <App />
      </ConfigProvider>
    </CacheProvider>
  </React.StrictMode>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
